import React from 'react'
import { yupResolver } from "@hookform/resolvers/yup";
import { Card, TextInput, Button, Radio, Label, Textarea, ButtonGroup } from "flowbite-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { adSchema } from "../schemas/ad.schema";
import { useNavigate, useParams } from "react-router-dom";
import useAds from "../hooks/useAds";
import { Attachment } from "../types/attachment";
import { useReducerAsState } from "../hooks/useReducerAsState";
import { FileManager } from "../components";
import { toast } from 'react-toastify';
import { routes } from '../routes';
import { useAuth } from '../context/authContext';

enum AdsTypeOptions {
    CODE = 'CODE',
    IMAGE = 'IMAGE',
}

enum FileSection {
    DESKTOP = 'DESKTOP',
    MOBILE = 'MOBILE',
    APP = 'APP',
}

interface State {
    desktopAttachment: Attachment | null;
    mobileAttachment: Attachment | null;
    appAttachment: Attachment | null;
    type: AdsTypeOptions;
    showFileManager: boolean;
    fileSection: FileSection | null;
}

const AdsDetailsScreen = () => {
    const { permissions } = useAuth()

    const canCreate = permissions.find(item => item.slug === 'crear-publicidad')
    const canEdit = permissions.find(item => item.slug === 'editar-publicidad')
    
    const { id } = useParams();
    const { ad, findAd, saveAd } = useAds()

    const [state, setState] = useReducerAsState<State>({
        type: AdsTypeOptions.CODE,
        desktopAttachment: null,
        mobileAttachment: null,
        appAttachment: null,
        showFileManager: false,
        fileSection: null
    })

    const navigate = useNavigate();
    
    const { register, handleSubmit, setValue, getValues, watch, formState: { errors } } = useForm({
        resolver: yupResolver(adSchema),
    });

    watch("type")

    const openFileManager = (fileSection: FileSection) =>  setState({ 
        showFileManager: true,
        fileSection,
    })

    const handleCloseFileManager = () => setState({ showFileManager: false })

    const handleFileSelection = (file: Attachment) => {
        if(!file || !state.fileSection) return

        let attachmentKey;
        let formKey : 'desktop_attachment' | 'mobile_attachment' | 'app_attachment';

        switch (state.fileSection) {
            case FileSection.DESKTOP:
                attachmentKey = 'desktopAttachment';
                formKey = 'desktop_attachment'
                break;
            case FileSection.MOBILE:
                attachmentKey = 'mobileAttachment';
                formKey = 'mobile_attachment'
                break;
            case FileSection.APP:
                attachmentKey = 'appAttachment';
                formKey = 'app_attachment'
                break;
            default:
                return;
        }
    
        setState({ [attachmentKey]: file, showFileManager: false });
        setValue(formKey, file.id)
    }

    const save = async (data: any) => {
        if(!data) return

        const info = {
            id: id ? parseInt(id) : undefined,
            ...data
        }

        if(isNaN(parseInt(id!))) {
            // create
            if(canCreate){
                const saved = await saveAd(info)
                toast(saved ? "Publicidad guardada correctamente." : "Ocurrió un error al guardar la publicidad.",{
                    type: saved ? "success" : "error"
                });
                navigate(`${routes.DASHBOARD}/${routes.ADS}`, {
                    replace: true
                })
                return
            }else{
                toast("No tienes permiso para crear Publicidad",{
                    type: "error"
                });
                return
            }
        }

        if(id && !isNaN(parseInt(id))){
            if(canEdit){
                const saved = await saveAd(info)
                toast(saved ? "Publicidad guardada correctamente." : "Ocurrió un error al guardar la publicidad.",{
                    type: saved ? "success" : "error"
                });
                return
            }else{
                toast("No tienes permiso para actualizar Publicidad",{
                    type: "error"
                });
                return
            }
        }
    }

    const renderByType = () => {
        if(state.type === AdsTypeOptions.CODE){
            return <React.Fragment>
                <div className="flex flex-col mb-2">
                    <Label className="mb-2">Código de publicidad programática (Escritorio)</Label>
                    <Textarea 
                        placeholder="Escriba aquí" 
                        rows={4}
                        {...register("desktop_code")}
                        helperText={errors && errors.desktop_code ? errors.desktop_code.message : ""}
                        required 
                        color={errors && errors.desktop_code ? "failure" : "gray"}
                    />
                </div>
                <div className="flex flex-col mb-2">
                    <Label className="mb-2">Código de publicidad programática (Celular)</Label>
                    <Textarea 
                        placeholder="Escriba aquí" 
                        rows={4}
                        {...register("mobile_code")}
                        helperText={errors && errors.mobile_code ? errors.mobile_code.message : ""}
                        required 
                        color={errors && errors.mobile_code ? "failure" : "gray"}
                    />
                </div>
                <div className="flex flex-col mb-2">
                    <Label className="mb-2">Código de publicidad programática (Aplicación móvil)</Label>
                    <Textarea 
                        placeholder="Escriba aquí" 
                        rows={4}
                        {...register("app_code")}
                        helperText={errors && errors.app_code ? errors.app_code.message : ""}
                        required 
                        color={errors && errors.app_code ? "failure" : "gray"}
                    />
                </div>
            </React.Fragment>
        }

        return <React.Fragment>
                <div className="flex flex-col mb-2">
                    <Label className="mb-2">Imagen programática (Escritorio)</Label>
                    <div className="flex flex-col space-y-4">
                        {state.desktopAttachment && <Card
                            style={{width: 320}} 
                            imgAlt={state.desktopAttachment.title}
                            imgSrc={`${process.env.REACT_APP_UPLOADS}/${state.desktopAttachment?.url}`}
                        />}
                        <ButtonGroup>
                            <TextInput 
                                className='w-1/2'
                                placeholder="Enlace de la publicidad"
                                {...register("desktop_link")}
                                helperText={errors && errors.desktop_link ? errors.desktop_link.message : ""}
                                required 
                                color={errors && errors.desktop_link ? "failure" : "gray"} 
                            />
                            <Button onClick={() => openFileManager(FileSection.DESKTOP)} color="gray">Elija una imagen</Button>
                        </ButtonGroup>
                    </div>
                </div>
                <div className="flex flex-col mb-2">
                    <Label className="mb-2">Imagen programática (Celular)</Label>
                    <div className="flex flex-col space-y-4">
                        {state.mobileAttachment && <Card
                            style={{width: 320}} 
                            imgAlt={state.mobileAttachment.title}
                            imgSrc={`${process.env.REACT_APP_UPLOADS}/${state.mobileAttachment?.url}`}
                        />}
                        <ButtonGroup>
                            <TextInput 
                                className='w-1/2'
                                placeholder="Enlace de la publicidad"
                                {...register("mobile_link")}
                                helperText={errors && errors.mobile_link ? errors.mobile_link.message : ""}
                                required 
                                color={errors && errors.mobile_link ? "failure" : "gray"} 
                            />
                            <Button onClick={() => openFileManager(FileSection.MOBILE)} color="gray">Elija una imagen</Button>
                        </ButtonGroup>
                    </div>
                </div>
                <div className="flex flex-col mb-2">
                    <Label className="mb-2">Imagen programática (Aplicación móvil  ( mínimo 320x50 ) )</Label>
                    <div className="flex flex-col space-y-4">
                        {state.appAttachment && <Card
                            style={{width: 320}} 
                            imgAlt={state.appAttachment.title}
                            imgSrc={`${process.env.REACT_APP_UPLOADS}/${state.appAttachment?.url}`}
                        />}
                        <ButtonGroup>
                            <TextInput 
                                className='w-1/2'
                                placeholder="Enlace de la publicidad"
                                {...register("app_link")}
                                helperText={errors && errors.app_link ? errors.app_link.message : ""}
                                required 
                                color={errors && errors.app_link ? "failure" : "gray"} 
                            />
                            <Button onClick={() => openFileManager(FileSection.APP)} color="gray">Elija una imagen</Button>
                        </ButtonGroup>
                    </div>
                </div>
            </React.Fragment>
    }

    const setValues = () => {
        if(!ad) return

        setValue("title", ad.title)
        setValue("type", ad.type)
        setValue("desktop_code", ad.desktop_code || "")
        setValue("desktop_attachment", ad.desktop_attachment?.id || 0)
        setValue("desktop_link", ad.desktop_link || "")
        setValue("mobile_code", ad.mobile_code || "")
        setValue("mobile_attachment", ad.mobile_attachment?.id || 0)
        setValue("mobile_link", ad.mobile_link || "")
        setValue("app_code", ad.app_code || "")
        setValue("app_attachment", ad.app_attachment?.id || 0)
        setValue("app_link", ad.app_link || "")

        setState({
            desktopAttachment: ad.desktop_attachment || null,
            mobileAttachment: ad.mobile_attachment || null,
            appAttachment: ad.app_attachment || null,
            type: ad.type as AdsTypeOptions
        })
    }

    useEffect(() => {
        (async () => {
            if(!id) return

            const itemId = parseInt(id)
            if(!isNaN(itemId)){
                await findAd(itemId)
            }
        })()
    // eslint-disable-next-line
    },[])

    useEffect(() => {
        setValues()
        // eslint-disable-next-line
    },[ad])

    return <div className="m-4">
        <div className="mb-4">
            <div className="flex flex-row justify-between">
                <h1>Detalles de la publicidad</h1>
                <Button onClick={handleSubmit(save)}>Guardar publicidad</Button>
            </div>
        </div>
        
        <Card>
            <div className="flex flex-col mb-2">
                <Label className="mb-2">Título</Label>
                <TextInput 
                    placeholder="Escriba aquí"
                    {...register("title")}
                    helperText={errors && errors.title ? errors.title.message : ""}
                    required 
                    color={errors && errors.title ? "failure" : "gray"} 
                />
            </div>
            <div className="flex flex-col mb-2">
                <fieldset className="flex max-w-md flex-col gap-4">
                    <legend className="mb-4">Tipo de publicidad</legend>
                    <div className="flex items-center gap-2">
                        <Radio id="code" name="type" value="code" defaultChecked checked={getValues("type") === AdsTypeOptions.CODE} onChange={() => {
                            setState({
                                type:AdsTypeOptions.CODE,
                                desktopAttachment: null,
                                mobileAttachment: null,
                                appAttachment: null,
                            });
                            setValue("desktop_attachment", 0)
                            setValue("mobile_attachment", 0)
                            setValue("app_attachment", 0)
                            setValue("type", AdsTypeOptions.CODE);
                        }} />
                        <Label htmlFor="code">Publicidad programática</Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Radio id="image" name="type" value="image" checked={getValues("type") === AdsTypeOptions.IMAGE} onChange={() => {
                            setState({type:AdsTypeOptions.IMAGE});
                            setValue("desktop_code", "")
                            setValue("mobile_code", "")
                            setValue("app_code", "")
                            setValue("type", AdsTypeOptions.IMAGE)
                        }} />
                        <Label htmlFor="image">Imagen fija</Label>
                    </div>
                </fieldset>
            </div>
            {renderByType()}
        </Card>

        {state.showFileManager && <FileManager open onSelect={handleFileSelection} onClose={handleCloseFileManager} />}
    </div>
}


export default AdsDetailsScreen